export type Emoji = {
  char: string
  keywords: string
}

// The tab's tooltip is translated, so the category carries its key rather than English text.
type EmojiCategoryLabelKey =
  | "emojiPicker.categoryActivities"
  | "emojiPicker.categoryFlags"
  | "emojiPicker.categoryFood"
  | "emojiPicker.categoryNature"
  | "emojiPicker.categoryObjects"
  | "emojiPicker.categorySmileys"
  | "emojiPicker.categorySymbols"
  | "emojiPicker.categoryTravel"

export type EmojiCategory = {
  id: string
  labelKey: EmojiCategoryLabelKey
  icon: string
  emojis: Emoji[]
}

// Each line is one emoji followed by the words it can be searched by, which keeps
// the table below readable instead of a few hundred object literals.
const parseEmojis = (table: string): Emoji[] =>
  table
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => {
      const [char, ...keywords] = line.split(" ")

      return { char, keywords: keywords.join(" ") }
    })

export const emojiCategories: EmojiCategory[] = [
  {
    id: "smileys",
    labelKey: "emojiPicker.categorySmileys",
    icon: "😀",
    emojis: parseEmojis(`
      😀 grinning smile happy
      😃 smiley happy joy
      😄 laugh happy grin
      😁 beaming grin teeth
      😆 laughing satisfied haha
      😅 sweat laugh relief
      🤣 rofl rolling laughing
      😂 joy tears laughing
      🙂 slight smile
      🙃 upside down silly
      😉 wink flirt
      😊 blush smile happy
      😇 innocent halo angel
      🥰 love hearts adore
      😍 heart eyes love
      🤩 star struck amazed
      😘 kiss blow love
      😗 kissing
      😚 kissing closed eyes
      😋 yum tasty delicious
      😛 tongue playful
      😜 wink tongue joke
      🤪 zany crazy silly
      🤗 hug hugging
      🤭 giggle oops
      🤔 thinking hmm consider
      🤐 zipper mouth secret quiet
      😐 neutral meh
      😑 expressionless blank
      😶 no mouth silence
      😏 smirk sly
      😒 unamused annoyed
      🙄 eye roll whatever
      😬 grimace awkward
      😴 sleeping zzz tired
      🤤 drooling
      😪 sleepy tired
      😷 mask sick
      🤒 sick thermometer fever
      🤕 injured bandage hurt
      🤢 nauseated sick gross
      🥵 hot heat sweating
      🥶 cold freezing
      😵 dizzy knocked out
      🤯 mind blown exploding head
      🤠 cowboy
      🥳 partying celebrate birthday
      😎 sunglasses cool
      🤓 nerd geek glasses
      🧐 monocle inspect scrutiny
      😕 confused
      😟 worried
      🙁 frowning sad
      😮 open mouth surprised wow
      😯 hushed surprised
      😲 astonished shocked
      😳 flushed embarrassed
      🥺 pleading puppy eyes please
      😦 frowning open mouth
      😨 fearful scared
      😰 anxious worried sweat
      😥 sad disappointed relieved
      😢 cry sad tear
      😭 sobbing crying loudly
      😱 scream fear shocked
      😖 confounded frustrated
      😣 persevere struggle
      😞 disappointed sad
      😓 downcast sweat
      😩 weary tired
      😫 tired exhausted
      🥱 yawning bored
      😤 triumph steam frustrated
      😡 pouting angry rage mad
      😠 angry mad
      🤬 cursing swearing angry
      😈 smiling imp devil mischief
      💀 skull dead
      💩 poop
      🤡 clown
      👻 ghost boo
      👽 alien
      🤖 robot bot
      👋 wave hello hi bye
      🤚 raised back hand
      ✋ raised hand stop
      🖐 hand fingers splayed
      👌 ok perfect
      🤏 pinching small
      ✌️ victory peace
      🤞 crossed fingers luck hope
      🤟 love you gesture
      🤘 rock horns
      🤙 call me shaka
      👈 point left
      👉 point right
      👆 point up
      👇 point down
      ☝️ index up
      👍 thumbs up yes like approve
      👎 thumbs down no dislike
      ✊ fist raised
      👊 fist bump punch
      👏 clap applause bravo
      🙌 raising hands celebrate praise
      👐 open hands
      🤲 palms up
      🤝 handshake deal agree
      🙏 pray thanks please
      💪 muscle strong flex
      🧠 brain smart
      👀 eyes look see
      🗣 speaking head
      👶 baby
      🧒 child
      👦 boy
      👧 girl
      🧑 person adult
      👨 man
      👩 woman
      🧓 older person
      👮 police officer cop
      🕵️ detective spy
      👷 construction worker
      👸 princess
      🦸 superhero
      🧙 mage wizard
      🤶 mrs claus
      🎅 santa claus christmas
      💃 dancer dancing
      🕺 man dancing
      🚶 walking
      🏃 running run
      🧘 yoga meditation zen
    `),
  },
  {
    id: "nature",
    labelKey: "emojiPicker.categoryNature",
    icon: "🐻",
    emojis: parseEmojis(`
      🐶 dog puppy
      🐱 cat kitten
      🐭 mouse
      🐹 hamster
      🐰 rabbit bunny
      🦊 fox
      🐻 bear
      🐼 panda
      🐨 koala
      🐯 tiger
      🦁 lion
      🐮 cow
      🐷 pig
      🐸 frog
      🐵 monkey
      🙈 see no evil monkey
      🙉 hear no evil monkey
      🙊 speak no evil monkey
      🐔 chicken
      🐧 penguin
      🐦 bird
      🦆 duck
      🦅 eagle
      🦉 owl
      🦇 bat
      🐺 wolf
      🐗 boar
      🐴 horse
      🦄 unicorn
      🐝 bee honeybee
      🐛 bug caterpillar
      🦋 butterfly
      🐌 snail slow
      🐞 ladybug
      🐜 ant
      🕷 spider
      🐢 turtle
      🐍 snake
      🦎 lizard
      🐙 octopus
      🦑 squid
      🦐 shrimp
      🦀 crab
      🐡 blowfish
      🐠 tropical fish
      🐟 fish
      🐬 dolphin
      🐳 whale
      🦈 shark
      🐊 crocodile
      🐅 tiger
      🦓 zebra
      🦍 gorilla
      🐘 elephant
      🐪 camel
      🦒 giraffe
      🐄 cow
      🐖 pig
      🐑 sheep
      🐐 goat
      🦌 deer
      🐕 dog
      🐈 cat
      🐓 rooster
      🦃 turkey
      🕊 dove peace
      🐇 rabbit
      🐿 chipmunk squirrel
      🌵 cactus
      🎄 christmas tree
      🌲 evergreen tree
      🌳 tree
      🌴 palm tree
      🌱 seedling sprout growth
      🌿 herb leaf
      ☘️ shamrock clover
      🍀 four leaf clover luck
      🍁 maple leaf autumn
      🍂 fallen leaves autumn
      🍃 leaf wind
      🌺 hibiscus flower
      🌸 cherry blossom flower
      🌼 blossom flower
      🌻 sunflower
      🌹 rose flower
      🥀 wilted flower
      🌷 tulip
      💐 bouquet flowers
      🌍 earth globe world
      🌙 crescent moon night
      ⭐ star
      🌟 glowing star sparkle
      ✨ sparkles shiny magic
      ⚡ zap lightning fast
      🔥 fire hot lit flame
      💥 boom explosion collision
      🌈 rainbow
      ☀️ sun sunny
      ⛅ partly cloudy
      ☁️ cloud
      🌧 rain
      ⛈ thunderstorm
      ❄️ snowflake cold
      ⛄ snowman
      💧 droplet water
      🌊 wave ocean water
    `),
  },
  {
    id: "food",
    labelKey: "emojiPicker.categoryFood",
    icon: "🍔",
    emojis: parseEmojis(`
      🍏 green apple
      🍎 apple
      🍐 pear
      🍊 tangerine orange
      🍋 lemon
      🍌 banana
      🍉 watermelon
      🍇 grapes
      🍓 strawberry
      🫐 blueberries
      🍒 cherries
      🍑 peach
      🥭 mango
      🍍 pineapple
      🥥 coconut
      🥝 kiwi
      🍅 tomato
      🥑 avocado
      🍆 eggplant
      🥔 potato
      🥕 carrot
      🌽 corn
      🌶 hot pepper spicy
      🥒 cucumber
      🥬 leafy green lettuce
      🥦 broccoli
      🧄 garlic
      🧅 onion
      🍄 mushroom
      🥜 peanuts nuts
      🌰 chestnut
      🍞 bread
      🥐 croissant
      🥖 baguette bread
      🥨 pretzel
      🧀 cheese
      🥚 egg
      🍳 cooking fried egg
      🥞 pancakes
      🧇 waffle
      🥓 bacon
      🍔 hamburger burger
      🍟 fries
      🍕 pizza
      🌭 hot dog
      🥪 sandwich
      🌮 taco
      🌯 burrito
      🥙 pita wrap
      🧆 falafel
      🥘 paella pan of food
      🍝 spaghetti pasta
      🍜 ramen noodles
      🍲 stew pot food
      🍛 curry rice
      🍣 sushi
      🍱 bento box
      🥟 dumpling
      🍤 fried shrimp
      🍚 rice
      🍙 rice ball
      🍘 rice cracker
      🥗 salad
      🍿 popcorn
      🧈 butter
      🧂 salt
      🥫 canned food
      🍦 ice cream soft serve
      🍩 doughnut donut
      🍪 cookie
      🎂 birthday cake
      🍰 cake shortcake
      🧁 cupcake
      🥧 pie
      🍫 chocolate
      🍬 candy sweet
      🍭 lollipop
      🍮 custard pudding
      🍯 honey
      🍼 baby bottle milk
      🥛 milk glass
      ☕ coffee hot beverage tea
      🍵 tea green
      🧃 juice box
      🥤 cup straw soda
      🍶 sake
      🍺 beer
      🍻 cheers beers
      🥂 clinking glasses celebrate toast
      🍷 wine
      🥃 whisky tumbler
      🍸 cocktail martini
      🍹 tropical drink
      🧉 mate
      🧊 ice cube
      🍽 plate fork knife dining
      🥄 spoon
      🔪 knife cooking
    `),
  },
  {
    id: "activities",
    labelKey: "emojiPicker.categoryActivities",
    icon: "⚽",
    emojis: parseEmojis(`
      ⚽ soccer football
      🏀 basketball
      🏈 american football
      ⚾ baseball
      🥎 softball
      🎾 tennis
      🏐 volleyball
      🏉 rugby
      🥏 flying disc frisbee
      🎱 8 ball pool billiards
      🪀 yo yo
      🏓 ping pong table tennis
      🏸 badminton
      🏒 ice hockey
      🏑 field hockey
      🥍 lacrosse
      🏏 cricket
      🥅 goal net
      ⛳ golf flag
      🏹 bow arrow archery
      🎣 fishing
      🤿 diving mask
      🥊 boxing glove
      🥋 martial arts
      🎽 running shirt
      🛹 skateboard
      🛼 roller skate
      ⛸ ice skate
      🥌 curling stone
      🎿 ski
      ⛷ skier
      🏂 snowboarder
      🏋️ weight lifting gym
      🤸 cartwheel gymnastics
      🤼 wrestling
      🤽 water polo
      🤾 handball
      🤺 fencing
      🏇 horse racing
      🧗 climbing
      🚴 cycling bike
      🚵 mountain biking
      🏆 trophy win champion
      🥇 first place gold medal
      🥈 second place silver medal
      🥉 third place bronze medal
      🏅 medal award
      🎖 military medal honour
      🎗 reminder ribbon
      🎫 ticket
      🎟 admission tickets
      🎪 circus tent
      🤹 juggling
      🎭 performing arts theatre
      🎨 art palette painting
      🎬 clapper board film
      🎤 microphone sing karaoke
      🎧 headphones music
      🎼 musical score
      🎹 piano keyboard
      🥁 drum
      🎷 saxophone
      🎺 trumpet
      🎸 guitar
      🎻 violin
      🎲 game die dice
      ♟ chess pawn
      🎯 direct hit bullseye target
      🎮 video game controller
      🕹 joystick
      🎰 slot machine
      🧩 puzzle piece
      🎉 party popper celebrate congrats
      🎊 confetti ball celebrate
      🎈 balloon
      🎁 gift present
      🎀 ribbon
      🪄 magic wand
      🎆 fireworks
      🎇 sparkler
      🧨 firecracker dynamite
    `),
  },
  {
    id: "travel",
    labelKey: "emojiPicker.categoryTravel",
    icon: "✈️",
    emojis: parseEmojis(`
      🚗 car automobile
      🚕 taxi
      🚙 suv
      🚌 bus
      🚎 trolleybus
      🏎 racing car
      🚓 police car
      🚑 ambulance
      🚒 fire engine
      🚐 minibus van
      🚚 delivery truck
      🚛 lorry truck
      🚜 tractor
      🛴 kick scooter
      🚲 bicycle bike
      🛵 motor scooter
      🏍 motorcycle
      🚨 police light siren alert
      🚔 police car
      🚍 oncoming bus
      🚘 oncoming car
      🚖 oncoming taxi
      🚡 aerial tramway
      🚠 mountain cableway
      🚟 suspension railway
      🚃 railway car
      🚋 tram
      🚞 mountain railway
      🚄 bullet train
      🚅 high speed train
      🚈 light rail
      🚂 locomotive steam train
      🚆 train
      🚇 metro subway
      🚊 tram
      🚉 station
      ✈️ airplane flight travel
      🛫 departure takeoff
      🛬 arrival landing
      🛩 small airplane
      💺 seat
      🚁 helicopter
      🚀 rocket launch ship
      🛸 flying saucer ufo
      🛶 canoe
      ⛵ sailboat
      🚤 speedboat
      🛥 motor boat
      🛳 passenger ship
      ⛴ ferry
      🚢 ship
      ⚓ anchor
      🚧 construction barrier
      ⛽ fuel pump gas
      🚏 bus stop
      🗺 world map
      🗿 moai statue
      🗽 statue of liberty
      🗼 tokyo tower
      🏰 castle
      🏯 japanese castle
      🏟 stadium
      🎡 ferris wheel
      🎢 roller coaster
      🎠 carousel horse
      ⛲ fountain
      ⛱ umbrella on ground beach
      🏖 beach
      🏝 desert island
      🏜 desert
      🌋 volcano
      ⛰ mountain
      🏔 snow capped mountain
      🗻 mount fuji
      🏕 camping
      ⛺ tent
      🏠 house home
      🏡 house with garden
      🏘 houses
      🏚 derelict house
      🏗 building construction
      🏭 factory
      🏢 office building
      🏬 department store
      🏣 post office
      🏤 post office
      🏥 hospital
      🏦 bank
      🏨 hotel
      🏪 convenience store
      🏫 school
      💒 wedding
      🏛 classical building
      ⛪ church
      🕌 mosque
      🕍 synagogue
      🛕 hindu temple
      🌆 cityscape dusk
      🌇 sunset
      🌃 night stars city
      🌉 bridge at night
      🌌 milky way
      🎑 moon viewing ceremony
    `),
  },
  {
    id: "objects",
    labelKey: "emojiPicker.categoryObjects",
    icon: "💡",
    emojis: parseEmojis(`
      ⌚ watch
      📱 mobile phone
      💻 laptop computer
      ⌨️ keyboard
      🖥 desktop computer
      🖨 printer
      🖱 computer mouse
      💽 minidisc
      💾 floppy disk save
      💿 optical disk cd
      📀 dvd
      📷 camera photo
      📸 camera flash
      📹 video camera
      🎥 movie camera
      📽 film projector
      📞 telephone receiver call
      ☎️ telephone
      📟 pager
      📠 fax
      📺 television tv
      📻 radio
      🎙 studio microphone podcast
      ⏱ stopwatch
      ⏰ alarm clock
      ⏳ hourglass waiting time
      ⌛ hourglass done time
      🔋 battery
      🔌 electric plug
      💡 light bulb idea
      🔦 flashlight torch
      🕯 candle
      🧯 fire extinguisher
      🛢 oil drum
      💸 money with wings spending
      💵 dollar money cash
      💴 yen
      💶 euro
      💷 pound
      💰 money bag
      💳 credit card payment
      🧾 receipt invoice
      💎 gem diamond
      ⚖️ balance scale justice
      🧰 toolbox
      🔧 wrench fix
      🔨 hammer
      ⚒ hammer and pick
      🛠 hammer and wrench tools
      ⛏ pick
      🔩 nut and bolt
      ⚙️ gear settings
      🧱 brick
      ⛓ chains
      🧲 magnet
      🔫 water pistol
      💣 bomb
      🪓 axe
      🔮 crystal ball fortune
      🧿 nazar amulet
      ⚗️ alembic chemistry
      🔭 telescope
      🔬 microscope
      🕳 hole
      💊 pill medicine
      💉 syringe injection
      🩸 blood drop
      🩹 adhesive bandage plaster
      🩺 stethoscope
      🚪 door
      🛏 bed
      🛋 couch and lamp
      🪑 chair
      🚽 toilet
      🚿 shower
      🛁 bathtub
      🧴 lotion bottle
      🧷 safety pin
      🧹 broom cleaning
      🧺 basket laundry
      🧻 toilet paper roll
      🧼 soap
      🧽 sponge
      🛒 shopping cart
      🚬 cigarette
      ⚰️ coffin
      🏷 label tag
      🔖 bookmark
      📄 page document file
      📃 page with curl
      📑 bookmark tabs
      📊 bar chart graph stats
      📈 chart increasing growth up
      📉 chart decreasing down loss
      📋 clipboard
      📌 pushpin pin
      📍 round pushpin location
      📎 paperclip attachment
      🖇 linked paperclips
      📐 triangular ruler
      📏 straight ruler
      ✂️ scissors cut
      🗃 card file box
      🗄 file cabinet
      🗑 wastebasket trash delete
      🔒 locked lock secure
      🔓 unlocked
      🔐 locked with key
      🔑 key
      🗝 old key
      📝 memo note write
      ✏️ pencil write edit
      🖊 pen
      🖍 crayon
      🖌 paintbrush
      📚 books library
      📖 open book read
      📕 closed book
      📓 notebook
      📒 ledger
      📔 notebook decorative
      📰 newspaper news
      🗞 rolled up newspaper
      ✉️ envelope email mail
      📧 e mail
      📨 incoming envelope
      📩 envelope with arrow
      📤 outbox tray sent
      📥 inbox tray
      📦 package box shipping
      📫 mailbox
      📮 postbox
      🔔 bell notification
      🔕 bell with slash muted
      📢 loudspeaker announcement
      📣 megaphone shout
      🔍 magnifying glass search
      🔎 magnifying glass search
      🗓 spiral calendar
      📅 calendar date
      📆 tear off calendar
      🕐 clock time
    `),
  },
  {
    id: "symbols",
    labelKey: "emojiPicker.categorySymbols",
    icon: "❤️",
    emojis: parseEmojis(`
      ❤️ red heart love
      🧡 orange heart
      💛 yellow heart
      💚 green heart
      💙 blue heart
      💜 purple heart
      🖤 black heart
      🤍 white heart
      🤎 brown heart
      💔 broken heart
      ❣️ heart exclamation
      💕 two hearts
      💞 revolving hearts
      💓 beating heart
      💗 growing heart
      💖 sparkling heart
      💘 heart with arrow cupid
      💝 heart with ribbon gift
      💟 heart decoration
      ☮️ peace
      ✝️ latin cross
      ☪️ star and crescent
      🕉 om
      ☸️ wheel of dharma
      ✡️ star of david
      🔯 six pointed star
      ☯️ yin yang
      ♈ aries
      ♉ taurus
      ♊ gemini
      ♋ cancer
      ♌ leo
      ♍ virgo
      ♎ libra
      ♏ scorpio
      ♐ sagittarius
      ♑ capricorn
      ♒ aquarius
      ♓ pisces
      🆔 id
      ⚛️ atom science
      ☢️ radioactive
      ☣️ biohazard
      📴 mobile phone off
      📳 vibration mode
      🈶 japanese not free
      ✴️ eight pointed star
      🆚 versus vs
      💮 white flower
      🉐 japanese bargain
      ㊙️ japanese secret
      🈺 japanese open for business
      🅰️ a button blood type
      🆎 ab button blood type
      🅱️ b button blood type
      🆑 cl button
      🆘 sos help emergency
      ❌ cross mark no wrong
      ⭕ hollow red circle
      🛑 stop sign
      ⛔ no entry
      📛 name badge
      🚫 prohibited no
      💯 hundred points perfect score
      💢 anger symbol
      ♨️ hot springs
      🚷 no pedestrians
      🚯 no littering
      🚳 no bicycles
      🚱 non potable water
      🔞 no one under eighteen
      📵 no mobile phones
      ❗ exclamation mark important
      ❕ white exclamation
      ❓ question mark
      ❔ white question mark
      ‼️ double exclamation
      ⁉️ exclamation question
      🔅 dim button
      🔆 bright button
      〽️ part alternation mark
      ⚠️ warning caution
      🚸 children crossing
      🔱 trident
      ⚜️ fleur de lis
      🔰 japanese symbol for beginner
      ♻️ recycling
      ✅ check mark button done yes correct
      ☑️ check box with check done
      ✔️ check mark done
      ➕ plus add
      ➖ minus
      ➗ divide
      ✖️ multiply
      💲 heavy dollar sign
      💱 currency exchange
      ©️ copyright
      ®️ registered
      ™️ trade mark
      🔚 end arrow
      🔙 back arrow
      🔛 on arrow
      🔝 top arrow
      🔜 soon arrow
      ⏩ fast forward
      ⏪ fast reverse
      ⏫ fast up
      ⏬ fast down
      ▶️ play
      ⏸ pause
      ⏹ stop
      ⏺ record
      ⏏️ eject
      🔀 shuffle
      🔁 repeat loop
      🔂 repeat one
      🔼 upwards button
      🔽 downwards button
      ⬆️ up arrow
      ⬇️ down arrow
      ⬅️ left arrow
      ➡️ right arrow
      ↗️ up right arrow
      ↘️ down right arrow
      ↙️ down left arrow
      ↖️ up left arrow
      ↕️ up down arrow
      ↔️ left right arrow
      ↩️ right arrow curving left reply
      ↪️ left arrow curving right forward
      🔄 counterclockwise arrows refresh sync
      🔃 clockwise vertical arrows
      🔴 red circle
      🟠 orange circle
      🟡 yellow circle
      🟢 green circle
      🔵 blue circle
      🟣 purple circle
      ⚫ black circle
      ⚪ white circle
      🟥 red square
      🟧 orange square
      🟨 yellow square
      🟩 green square
      🟦 blue square
      🟪 purple square
      ⬛ black square
      ⬜ white square
      🔶 large orange diamond
      🔷 large blue diamond
      🔸 small orange diamond
      🔹 small blue diamond
      🔺 red triangle up
      🔻 red triangle down
      💠 diamond with a dot
      🔘 radio button
      🔳 white square button
      🔲 black square button
    `),
  },
  {
    id: "flags",
    labelKey: "emojiPicker.categoryFlags",
    icon: "🏁",
    emojis: parseEmojis(`
      🏁 chequered flag finish race
      🚩 triangular flag
      🎌 crossed flags japan
      🏴 black flag
      🏳️ white flag surrender
      🏳️‍🌈 rainbow flag pride lgbt
      🏴‍☠️ pirate flag jolly roger
      🇦🇷 argentina flag
      🇦🇺 australia flag
      🇦🇹 austria flag
      🇧🇩 bangladesh flag
      🇧🇪 belgium flag
      🇧🇷 brazil flag
      🇧🇬 bulgaria flag
      🇨🇦 canada flag
      🇨🇱 chile flag
      🇨🇳 china flag
      🇨🇴 colombia flag
      🇭🇷 croatia flag
      🇨🇾 cyprus flag
      🇨🇿 czechia czech republic flag
      🇩🇰 denmark flag
      🇪🇬 egypt flag
      🇪🇪 estonia flag
      🇪🇹 ethiopia flag
      🇫🇮 finland suomi flag
      🇫🇷 france flag
      🇩🇪 germany deutschland flag
      🇬🇭 ghana flag
      🇬🇷 greece flag
      🇭🇰 hong kong flag
      🇭🇺 hungary flag
      🇮🇸 iceland flag
      🇮🇳 india flag
      🇮🇩 indonesia flag
      🇮🇷 iran flag
      🇮🇶 iraq flag
      🇮🇪 ireland flag
      🇮🇱 israel flag
      🇮🇹 italy flag
      🇯🇲 jamaica flag
      🇯🇵 japan flag
      🇯🇴 jordan flag
      🇰🇿 kazakhstan flag
      🇰🇪 kenya flag
      🇰🇼 kuwait flag
      🇱🇻 latvia flag
      🇱🇧 lebanon flag
      🇱🇹 lithuania flag
      🇱🇺 luxembourg flag
      🇲🇾 malaysia flag
      🇲🇹 malta flag
      🇲🇽 mexico flag
      🇲🇦 morocco flag
      🇳🇵 nepal flag
      🇳🇱 netherlands holland flag
      🇳🇿 new zealand flag
      🇳🇬 nigeria flag
      🇳🇴 norway flag
      🇵🇰 pakistan flag
      🇵🇸 palestine flag
      🇵🇦 panama flag
      🇵🇪 peru flag
      🇵🇭 philippines flag
      🇵🇱 poland flag
      🇵🇹 portugal flag
      🇶🇦 qatar flag
      🇷🇴 romania flag
      🇷🇺 russia flag
      🇸🇦 saudi arabia flag
      🇷🇸 serbia flag
      🇸🇬 singapore flag
      🇸🇰 slovakia flag
      🇸🇮 slovenia flag
      🇿🇦 south africa flag
      🇰🇷 south korea flag
      🇪🇸 spain espana flag
      🇱🇰 sri lanka flag
      🇸🇪 sweden flag
      🇨🇭 switzerland flag
      🇹🇼 taiwan flag
      🇹🇿 tanzania flag
      🇹🇭 thailand flag
      🇹🇷 turkey turkiye flag
      🇺🇦 ukraine flag
      🇦🇪 united arab emirates uae flag
      🇬🇧 united kingdom uk britain flag
      🇺🇸 united states usa america flag
      🇺🇾 uruguay flag
      🇻🇪 venezuela flag
      🇻🇳 vietnam flag
      🇪🇺 european union eu flag
      🏴󠁧󠁢󠁥󠁮󠁧󠁿 england flag
      🏴󠁧󠁢󠁳󠁣󠁴󠁿 scotland flag
      🏴󠁧󠁢󠁷󠁬󠁳󠁿 wales flag
    `),
  },
]

const allEmojis = emojiCategories.flatMap((category) => category.emojis)

export const searchEmojis = (term: string): Emoji[] => {
  const query = term.trim().toLowerCase()

  if (!query) return []

  return allEmojis.filter((emoji) => emoji.keywords.includes(query))
}

// Emoji-only messages are rendered larger, the way most chat clients do. More than
// a handful of them is a regular message again, not a reaction-style one.
const MAX_ENLARGED_EMOJIS = 3

// Assembled from strings rather than written as regex literals: TypeScript rejects
// the unicode (`u`) flag while the project targets es5, even though every browser we
// build for has supported it for years.
const PICTOGRAPH = "\\p{Extended_Pictographic}"
// Country flags are a pair of regional indicators rather than a pictograph of their own.
const REGIONAL_INDICATOR = "\\p{Regional_Indicator}"
// Skin tone modifiers, zero-width joiners, variation selectors and the tag characters
// that spell out subdivision flags are parts of an emoji rather than characters of
// their own, so they may sit alongside the pictographs.
const EMOJI_GLUE = "\\p{Emoji_Modifier}|[\\u200D\\uFE0F\\u{E0020}-\\u{E007F}\\s]"

const pictograph = new RegExp(PICTOGRAPH, "gu")
const regionalIndicator = new RegExp(REGIONAL_INDICATOR, "gu")
const emojiOnly = new RegExp(`^(?:${PICTOGRAPH}|${REGIONAL_INDICATOR}|${EMOJI_GLUE})+$`, "u")

export const isEmojiOnlyMessage = (content: string): boolean => {
  const text = content.trim()

  if (!text || !emojiOnly.test(text)) return false

  const flags = (text.match(regionalIndicator) ?? []).length / 2
  const count = (text.match(pictograph) ?? []).length + flags

  return count > 0 && count <= MAX_ENLARGED_EMOJIS
}
